<template>
    <FormInputContainer class="FormSearchSelect" :inputId="`I${_uid}`" :label="label" :errors="errors" :required="required">
        <v-select
            ref="vSelect"
            v-bind="$attrs"
            :value="value"
            :append-to-body="true"
            :calculate-position="withPopper"
            @input="$emit('input', $event)"
        >
            <template v-for="(_, slot) of $slots" v-slot:[slot]="scope">
                <div
                    @mouseenter="clearHighlight"
                    :class="{ 'slot-wrapper': ['list-header', 'list-footer'].includes(slot) }"
                >
                    <slot :name="slot" v-bind="scope" />
                </div>
            </template>
        </v-select>
    </FormInputContainer>
</template>

<script>
import vSelect from 'vue-select';
import FormInputContainer from './FormInputContainer';
import { createPopper } from '@popperjs/core';

export default {
    name: 'FormSearchSelect',

    components: {
        'v-select': vSelect,
        FormInputContainer,
    },

    props: {
        value: { type: [String, Number, Object] },
        label: { type: String },
        nullable: { type: Boolean },
        errors: { type: Array },
        required: { type: Boolean, default: false },
    },

    methods: {
        // https://vue-select.org/guide/positioning.html#popper-js-integration
        withPopper(dropdownList, component, { width }) {
            // Set the dropdown width to be at least as wide as the toggle, but allow it to grow
            const minWidth = component.$refs.toggle.getBoundingClientRect().width;
            dropdownList.style.minWidth = `${minWidth}px`;
            dropdownList.style.width = 'auto';
            // dropdownList.style.maxWidth = '500px';
            // dropdownList.style.zIndex = 'var(--z-index-popover)';

            // Create popper instance with smart positioning
            const popper = createPopper(component.$refs.toggle, dropdownList, {
                placement: 'bottom-start',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, -1],
                        },
                    },
                    {
                        name: 'flip',
                        options: {
                            boundary: 'viewport',
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            boundary: 'viewport',
                            padding: 8,
                        },
                    },
                ],
            });

            return () => popper.destroy();
        },

        clearHighlight() {
            // Prevents vue-select from highlighting options when hovering over custom slot content
            if (this.$refs.vSelect) {
                this.$refs.vSelect.typeAheadPointer = -1;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~vue-select/dist/vue-select.css';
@import 'globals.scss';

$arrow-box-width: 30px;
$input-right-padding: $arrow-box-width + $input-padding-horizontal;

.FormSearchSelect {
    ::v-deep {
        #vs1__combobox,
        input.vs__search,
        .vs__dropdown-toggle,
        .vs__dropdown-menu,
        .vs__selected-options,
        .vs__selected {
            margin: 0;
            padding: 0;
        }

        .v-select {
            @extend %form-control;
            padding: 0;
        }

        input.vs__search {
            padding: 0 $input-right-padding 0 $input-padding-horizontal !important;
            border-radius: $border-radius;
            cursor: pointer;
        }

        .vs__dropdown-toggle {
            height: 100%;
            border: none;
            overflow: hidden;
        }

        .vs__selected {
            overflow: hidden;
            white-space: nowrap;
            opacity: 1;
        }

        // Selected state when dropdown is open
        .vs--open .vs__selected {
            position: static;
            border-radius: 0;
            align-self: center; // Needed to apply background color tightly around text
            background: Highlight;
            color: HighlightText;
        }

        // Hide close icon
        .vs__clear {
            display: none;
        }

        .vs__selected-options {
            width: 100%;
            flex-wrap: nowrap;
            cursor: pointer;
        }

        // .vs__selected appears if user selected an option
        .vs__selected-options:has(.vs__selected) {
            .vs__selected {
                max-width: calc(100% - #{$input-right-padding} - #{$input-padding-horizontal});
                margin-left: $input-padding-horizontal;
            }

            input {
                // Places cursor directly to the right of selected text
                padding-left: 0 !important;
            }
        }

        .vs__actions {
            @include absolute-center(y);
            right: 0;
            width: $input-right-padding;
            height: 100%;
            padding-left: $input-padding-horizontal;
            border-style: solid;
            border-color: $divider-color;
            border-width: 0 0 0 1px;
            background-color: $bg-color-2;
            border-top-right-radius: $border-radius;
            border-bottom-right-radius: $border-radius;
            cursor: pointer;
        }
    }

    &.hasErrors {
        ::v-deep .v-select {
            border: none !important;
        }

        ::v-deep .vs__search {
            border-style: solid;
            border-width: 1px;
        }
    }
}
</style>

<!-- Unscoped due to Popper -->
<style lang="scss">
@import 'globals.scss';

.vs__dropdown-menu {
    width: auto;
    // Disable max width for now, we can enable it here if it becomes a problem
    // max-width: 600px;
    border: 1px solid $divider-color;
    z-index: $z-index-popover;
}

.vs__dropdown-option {
    padding: 5px $input-padding-horizontal;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: $text-color-base;

    &.vs__dropdown-option--highlight {
        background-color: Highlight;
        color: HighlightText;
    }
}
</style>